'use client'

// His shelves, at his roosts.
//
// One shelf at nest_rest until 3 Nov, for what he found at the junk heap.
// Sam, 3 Nov: "perhaps we give multiple shelves, since he has multiple
// roosts now", and of his bag, "he can move his items around to different
// places". So a shelf at each container roost (lib/world/shelves.js, read
// off the blend), each holding twelve things: his finds and the things
// people gave him alike.
//
// The sim keeps what is on each (world/Hoard.js) and mirrors it onto that
// roost's nest entity each tick, as rows of { slot, kind, colour } for a
// find and { slot, itemId, spec } for a thing of his. This draws them in
// Sam's nooks: hoard.glb, two old fruit crates on their sides with a third
// on top and a plank across, four times over, laid out in Blender beside
// the open containers at world coordinates, so it renders at IDENTITY like
// the greenhouse (one mesh in four places, so one instanced draw). Each
// shelf's twelve empties are where things go, named by its `prefix` and
// numbered from 1 in the order the sim fills them (slot index + 1): slot_1
// at nest_rest, rest_2_slot_1 at nest_rest_2, and so on.
//
// A find is trinket_<kind> from trinkets.glb, standing on its base at the
// slot, or the primitive shape lib/world/trinkets.js gives a kind with no
// mesh yet. A thing of his is drawn from the model lib/things/looks.js
// says draws it: a meal from the pieces it was built of (the same pieces
// and materials as on the ground, BoughtThings.jsx), a thing out of the
// vending machine from the machine's own model; each brought down to the
// size of a slot and never up. A thing nothing can draw (the old shop's
// blanket) is not drawn, as the pop-up shows its name instead. The models
// for his things load only once one is on a shelf.
//
// The nooks are furniture and always stand. The things on them render only
// from what the sim says: no sim, nothing on them, because what is on them
// IS the record of what he keeps. Nothing here runs per frame; small
// things cast no shadow (docs/world-performance.md: things under about
// 0.6 m do not, and there can be forty-eight of them now).

import { Suspense, memo, useMemo } from 'react'
import * as THREE from 'three/webgpu'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import useGameStore from '../../../stores/gameStore'
import { ditherMaterial } from '../effects/ditherRegistry'
import { useModel } from '../hooks/useModel'
import { useSceneNodeMaterials, toNode } from '../hooks/useSceneNodeMaterials'
import { trinketLook } from '../../../lib/world/trinkets'
import { SHELVES, SHELF_NESTS } from '../../../lib/world/shelves'
import { HOME_NEST } from '../../../lib/things/bag'
import { itemLook, VEND_MODEL } from '../../../lib/things/looks'
import { useBuyPieces, buildThing } from './BoughtThings'

export const HOARD_NEST = HOME_NEST
const NOOK = '/models/hoard.glb'
const THINGS = '/models/trinkets.glb'
// the most a thing of his stands on a shelf, any way: a slot was made for a key or a shell (13 to 32 cm)
const ON_SHELF = 0.26

function trinketGeometry(look) {
  const s = look.size
  switch (look.shape) {
    case 'disc': return new THREE.CylinderGeometry(s / 2, s / 2, s * 0.22, 14)
    case 'ball': return new THREE.SphereGeometry(s / 2, 12, 10)
    case 'bar': return new THREE.BoxGeometry(s, s * 0.22, s * 0.28)
    case 'blade': return new THREE.BoxGeometry(s, s * 0.06, s * 0.3)
    case 'dome': return new THREE.SphereGeometry(s / 2, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2)
    case 'block': return new THREE.BoxGeometry(s, s * 0.45, s * 0.55)
    case 'ring': return new THREE.TorusGeometry(s / 2, s * 0.12, 8, 18)
    default: return new THREE.SphereGeometry(s / 2, 10, 8)
  }
}

/** Every shelf's slots as world transforms: nest -> (sim slot index 0-11 -> { position, quaternion }). */
export function slotsOf(scene) {
  const out = new Map(SHELVES.map((sh) => [sh.nest, new Map()]))
  if (!scene) return out
  const named = SHELVES.map((sh) => ({ nest: sh.nest, re: new RegExp(`^${sh.prefix}(\\d+)$`) }))
  scene.updateMatrixWorld(true)
  scene.traverse((o) => {
    for (const { nest, re } of named) {
      const m = re.exec(o.name || '')
      if (!m) continue
      const p = new THREE.Vector3(); const q = new THREE.Quaternion(); const s = new THREE.Vector3()
      o.matrixWorld.decompose(p, q, s)
      out.get(nest).set(Number(m[1]) - 1, { position: p, quaternion: q })
      return
    }
  })
  return out
}

// What is on every shelf, as the nests carry it: one list a shelf, in SHELVES order, null for a nest the sim has not
// sent. One pass over the entities, no find per nest.
function pickShelves(s) {
  const out = SHELF_NESTS.map(() => null)
  for (const e of s.entityStates) {
    const i = SHELF_NESTS.indexOf(e.id)
    if (i >= 0) out[i] = e.state?.hoard || []
  }
  return out
}

// the same row as far as a picture goes
function sameRow(x, y) {
  if (x.slot !== y.slot || x.kind !== y.kind || x.colour !== y.colour || x.itemId !== y.itemId) return false
  return x.spec === y.spec || JSON.stringify(x.spec ?? null) === JSON.stringify(y.spec ?? null)
}
function sameShelves(a, b) {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]; const y = b[i]
    if (x === y) continue
    if (!x || !y || x.length !== y.length) return false
    for (let j = 0; j < x.length; j++) if (!sameRow(x[j], y[j])) return false
  }
  return true
}

/**
 * A thing brought to a shelf: centred, standing on its base at the origin, no bigger than a slot takes, and casting no
 * shadow. `obj` is a fresh object of its own (a build, a clone), which this takes into a holder and returns.
 */
function standing(obj, max = ON_SHELF) {
  const holder = new THREE.Group()
  holder.add(obj)
  holder.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(holder)
  if (box.isEmpty()) return null
  const size = box.getSize(new THREE.Vector3())
  const centre = box.getCenter(new THREE.Vector3())
  obj.position.x -= centre.x
  obj.position.y -= box.min.y
  obj.position.z -= centre.z
  holder.scale.setScalar(Math.min(1, max / Math.max(size.x, size.y, size.z, 1e-6)))
  holder.traverse((o) => { if (o.isMesh) o.castShadow = false })
  return holder
}

const specKey = (spec) => JSON.stringify(spec ?? {})

// A meal on a shelf: the same build and materials as on the ground, made small.
const ShelfMeal = memo(function ShelfMeal({ thing, pieces, tints }) {
  const obj = useMemo(
    () => standing(buildThing(thing.itemId, thing.spec || {}, pieces, tints)),
    // (the spec arrives as a fresh object with every change of the nest; its text is what is the same)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [thing.itemId, specKey(thing.spec), pieces, tints],
  )
  if (!obj) return null
  return <primitive object={obj} position={thing.spot.position} quaternion={thing.spot.quaternion} />
})

function Meals({ things }) {
  const { pieces, tints } = useBuyPieces()
  return things.map((t) => <ShelfMeal key={t.key} thing={t} pieces={pieces} tints={tints} />)
}

// Out of the vending machine: the machine's own model, each kind of thing prepared the first time one is on a shelf (its
// materials converted and dithered at birth, one set for the model, shared by every copy) and cloned for each one.
const vendPrepared = new WeakMap() // the model's scene -> { cache, kinds: node -> holder or null }
function vendKind(scene, node) {
  if (!vendPrepared.has(scene)) vendPrepared.set(scene, { cache: new Map(), kinds: new Map() })
  const made = vendPrepared.get(scene)
  if (!made.kinds.has(node)) {
    const src = scene.getObjectByName(node)
    let ready = null
    if (src) {
      const body = src.clone(true)
      body.position.set(0, 0, 0)
      body.traverse((o) => {
        if (!o.isMesh) return
        const convert = (m) => {
          const had = made.cache.has(m)
          const nm = toNode(m, made.cache, 0.85)
          if (!had) ditherMaterial(nm)
          return nm
        }
        o.material = Array.isArray(o.material) ? o.material.map(convert) : convert(o.material)
      })
      ready = standing(body)
    }
    made.kinds.set(node, ready)
  }
  return made.kinds.get(node)
}

function Vended({ things }) {
  const { scene } = useModel(VEND_MODEL)
  return things.map((t) => <ShelfVended key={t.key} thing={t} source={scene ? vendKind(scene, t.vend) : null} />)
}

const ShelfVended = memo(function ShelfVended({ thing, source }) {
  const obj = useMemo(() => (source ? source.clone(true) : null), [source])
  if (!obj) return null
  return <primitive object={obj} position={thing.spot.position} quaternion={thing.spot.quaternion} />
})

export function Hoard() {
  // What is on each shelf, compared by what changes a picture.
  const shelves = useStoreWithEqualityFn(useGameStore, pickShelves, sameShelves)

  const { scene: nook } = useModel(NOOK)
  const { scene: things } = useModel(THINGS)
  useSceneNodeMaterials(nook, { roughness: 1 })

  const slots = useMemo(() => slotsOf(nook), [nook])

  // One node material per source material (the trinkets share one sheet),
  // dithered at birth
  const kinds = useMemo(() => {
    const out = new Map()
    if (!things) return out
    const cache = new Map()
    things.traverse((o) => {
      const m = /^trinket_([a-z]+)$/.exec(o.name || '')
      if (!m || !o.isMesh) return
      const material = toNode(o.material, cache, 0.9)
      ditherMaterial(material)
      out.set(m[1], { geometry: o.geometry, material })
    })
    return out
  }, [things])

  // Kinds nobody has modelled yet: the old shape, one geometry a kind and one material a colour, made once
  const fallback = useMemo(() => ({ geometry: new Map(), material: new Map() }), [])

  // Everything on every shelf, sorted into what draws it: a find, a meal, a thing out of the machine.
  const { finds, meals, vended } = useMemo(() => {
    const out = { finds: [], meals: [], vended: [] }
    SHELF_NESTS.forEach((nest, i) => {
      const rows = shelves[i]
      // (no sim, nothing on the shelf: what is on it is the record)
      if (!rows) return
      const at = slots.get(nest)
      for (const t of rows) {
        const spot = at?.get(Number(t.slot))
        if (!spot) continue
        const key = `${nest}-${t.slot}`
        if (t.itemId) {
          const look = itemLook(t.itemId, t.spec)
          if (look?.buy) out.meals.push({ key: `${key}-${t.itemId}`, itemId: t.itemId, spec: t.spec || {}, spot })
          else if (look?.vend) out.vended.push({ key: `${key}-${look.vend}`, vend: look.vend, spot })
          continue
        }
        const mesh = kinds.get(t.kind)
        if (mesh) { out.finds.push({ key: `${key}-${t.kind}`, ...mesh, spot, lift: 0 }); continue }
        const look = trinketLook(t.kind, t.colour)
        if (!fallback.material.has(look.colour)) {
          const m = new THREE.MeshStandardNodeMaterial({ color: look.colour, roughness: 0.9 })
          ditherMaterial(m)
          fallback.material.set(look.colour, m)
        }
        if (!fallback.geometry.has(t.kind)) fallback.geometry.set(t.kind, trinketGeometry(look))
        out.finds.push({ key: `${key}-${t.kind}`, geometry: fallback.geometry.get(t.kind), material: fallback.material.get(look.colour), spot, lift: look.size / 2 })
      }
    })
    return out
  }, [shelves, slots, kinds, fallback])

  return (
    <group name="hoard">
      <primitive object={nook} />
      {finds.map((t) => (
        <mesh
          key={t.key}
          geometry={t.geometry}
          material={t.material}
          position={[t.spot.position.x, t.spot.position.y + t.lift, t.spot.position.z]}
          quaternion={t.spot.quaternion}
        />
      ))}
      {meals.length > 0 && (
        <Suspense fallback={null}>
          <Meals things={meals} />
        </Suspense>
      )}
      {vended.length > 0 && (
        <Suspense fallback={null}>
          <Vended things={vended} />
        </Suspense>
      )}
    </group>
  )
}

export default Hoard
